import React from 'react';
import { format, isValid, parseISO } from 'date-fns';
import { CalendarDays } from 'lucide-react';

import { cn } from '../../utils/cn';
import { Calendar } from './calendar';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

const toIsoDate = (value) => {
  if (!value) return null;
  try {
    const parsed = parseISO(String(value));
    if (!isValid(parsed)) return null;
    return parsed;
  } catch (e) {
    return null;
  }
};

const formatIso = (date) => format(date, 'yyyy-MM-dd');
const formatDisplay = (date) => format(date, 'MM/dd/yyyy');

const resolveBounds = (pastYears, futureYears) => {
  const now = new Date();
  const start = new Date(now.getFullYear() - pastYears, 0, 1);
  const end = new Date(now.getFullYear() + futureYears, 11, 31);
  return { startMonth: start, endMonth: end };
};

const SingleDatePicker = ({
  value,
  onChange,
  placeholder = 'Select date',
  disabled = false,
  error = false,
  pastYears = 20,
  futureYears = 5,
  className,
}) => {
  const selected = React.useMemo(() => toIsoDate(value), [value]);
  const [open, setOpen] = React.useState(false);

  const { startMonth, endMonth } = React.useMemo(() => resolveBounds(pastYears, futureYears), [pastYears, futureYears]);

  const setDate = React.useCallback(
    (date) => {
      if (!onChange) return;
      if (!date) {
        onChange('');
        setOpen(false);
        return;
      }
      onChange(formatIso(date));
      setOpen(false);
    },
    [onChange],
  );

  const onToday = React.useCallback(() => {
    setDate(new Date());
  }, [setDate]);

  const onClear = React.useCallback(() => {
    setDate(null);
  }, [setDate]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn('dt-date-trigger', 'dt-input', error && 'dt-input-error', className)}
          disabled={disabled}
        >
          <span className={cn('dt-date-trigger-text', !selected && 'dt-date-trigger-placeholder')}>
            {selected ? formatDisplay(selected) : placeholder}
          </span>
          <CalendarDays className="dt-date-trigger-icon" />
        </button>
      </PopoverTrigger>

      <PopoverContent className="dt-date-popover" align="start">
        <div className="dt-date-popover-actions">
          <button type="button" className="dt-date-action" onClick={onToday} disabled={disabled}>
            Today
          </button>
          <button type="button" className="dt-date-action dt-date-action-clear" onClick={onClear} disabled={disabled}>
            Clear
          </button>
        </div>

        <Calendar
          mode="single"
          selected={selected || undefined}
          onSelect={(date) => setDate(date || null)}
          captionLayout="dropdown"
          navLayout="around"
          startMonth={startMonth}
          endMonth={endMonth}
          defaultMonth={selected || undefined}
        />
      </PopoverContent>
    </Popover>
  );
};

export default SingleDatePicker;

